import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Package } from "lucide-react";
import { ContentApi } from "../../../../shared/api/contentApi";
import { useContentContext } from "../../ContentContext";
import ParamValueField from "../../../../shared/paramEditor/ParamValueField";
import { normalizeValueByType } from "../../../../shared/paramEditor/normalizeValueByType";
import { parseOptionsJson } from "../../../../shared/paramEditor/parseOptionsJson";
import {
    UiDialogClose,
    UiDialogContent,
    UiDialogDescription,
    UiDialogRoot,
    UiDialogTitle,
    UiDialogHeader,
    UiDialogBody,
    UiDialogFooter,
} from "../../../../components/ui/ant/Dialog";
import {
    Button,
    Form,
    Input,
    Select,
    Switch,
    App,
    Checkbox,
    Col,
    Row,
    Flex,
    Layout,
    Card
} from 'antd';
import "antd/dist/reset.css";

const { Header, Footer, Sider, Content } = Layout;

export default function NewItemDialog({ open, onClose, onCreated }) {
    const { projectId, versionId, canMutate, selectedVersion } = useContentContext();
    const [systemName, setSystemName] = useState("");
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");

    const [groups, setGroups] = useState([]);
    const [parameters, setParameters] = useState([]);
    const [selectedGroups, setSelectedGroups] = useState([]);
    const [parameterRows, setParameterRows] = useState([]);

    const [loadingGroups, setLoadingGroups] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!open) return;

        setSystemName("");
        setName("");
        setDescription("");
        setSelectedGroups([]);
        setParameterRows([]);
        setError("");

        const ac = new AbortController();
        setLoadingGroups(true);

        Promise.all([
            ContentApi.getParameterGroups({ projectId, versionId, page: 1, pageSize: 1000 }, { signal: ac.signal }),
            ContentApi.getParametersPaged({ projectId, versionId, page: 1, pageSize: 1000 }, { signal: ac.signal }),
        ])
            .then(([groupsData, parametersData]) => {
                setGroups(Array.isArray(groupsData?.items) ? groupsData.items : []);
                setParameters(Array.isArray(parametersData?.items) ? parametersData.items : []);
            })
            .catch((e) => {
                if (!ac.signal.aborted) {
                    setError(e?.message || "Не удалось загрузить справочники");
                }
            })
            .finally(() => {
                if (!ac.signal.aborted) setLoadingGroups(false);
            });

        return () => ac.abort();
    }, [open, projectId, versionId]);

    const parametersById = useMemo(
        () => new Map(parameters.map((parameter) => [parameter.id || parameter.parameterId, parameter])),
        [parameters],
    );

    const canSubmit = useMemo(
        () => systemName.trim().length > 0 && name.trim().length > 0 && !submitting && canMutate,
        [systemName, name, submitting, canMutate],
    );

    const toggleGroup = (groupId) => {
        setSelectedGroups((prev) =>
            prev.includes(groupId) ? prev.filter((x) => x !== groupId) : [...prev, groupId],
        );
    };

    const addParameterRow = () => {
        setParameterRows((prev) => [...prev, { parameterId: "", value: "" }]);
    };

    const updateParameterRow = (index, patch) => {
        setParameterRows((prev) => prev.map((row, rowIndex) => (rowIndex === index ? { ...row, ...patch } : row)));
    };

    const removeParameterRow = (index) => {
        setParameterRows((prev) => prev.filter((_, rowIndex) => rowIndex !== index));
    };

    const onSubmit = async (e) => {
        e?.preventDefault?.();
        if (!canSubmit) return;

        setSubmitting(true);
        setError("");

        try {
            const created = await ContentApi.createItem(projectId, versionId, {
                systemName: systemName.trim(),
                name: name.trim(),
                description: description.trim() || null,
            });

            const entityId = created?.id;
            if (entityId && selectedGroups.length > 0) {
                await Promise.all(
                    selectedGroups.map((groupId) =>
                        ContentApi.attachParameterGroupToItem(projectId, versionId, entityId, { parameterGroupId: groupId }),
                    ),
                );
            }

            const valueRows = parameterRows
                .filter((row) => row.parameterId)
                .map((row) => {
                    const parameter = parametersById.get(row.parameterId);
                    const valueType = parameter?.valueTypeCode || parameter?.valueType;
                    const normalized = normalizeValueByType(valueType, row.value, parseOptionsJson(parameter?.optionsJson));
                    if (!normalized.ok) {
                        throw new Error(`${parameter?.name || parameter?.systemName || row.parameterId}: ${normalized.err}`);
                    }
                    return { parameterId: row.parameterId, value: normalized.str };
                });

            if (entityId && valueRows.length > 0) {
                await Promise.all(
                    valueRows.map((row) =>
                        ContentApi.setEntityValue(projectId, versionId, entityId, row.parameterId, { valueJson: row.value, localizedTrId: null }),
                    ),
                );
            }

            onCreated?.(created);
            onClose?.();
        } catch (e) {
            setError(e?.message || "Не удалось создать предмет");
        } finally {
            setSubmitting(false);
        }
    };

    if (!open) return null;

    return (
        <App>
            <UiDialogRoot
                open={open}
                onOpenChange={(next) => {
                    if (!next) onClose?.();
                }}
            >
                <UiDialogContent size="lg">
                    <UiDialogHeader>
                        <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-emerald-500 to-teal-600 flex items-center justify-center text-white">
                                <Package size={20} />
                            </div>
                            <div>
                                <UiDialogTitle>Создать предмет</UiDialogTitle>
                                <UiDialogDescription>
                                    Создание сущности и привязка групп параметров.
                                </UiDialogDescription>
                            </div>
                        </div>
                        <UiDialogClose showIcon />
                    </UiDialogHeader>

                    <UiDialogBody>
                        {error && (
                            <div className="mb-4 rounded-xl border border-red-300 bg-red-50 text-red-700 p-3 text-sm">
                                {error}
                            </div>
                        )}

                        {!canMutate && (
                            <div className="mb-4 rounded-xl border border-amber-300 bg-amber-50 text-amber-700 p-3 text-sm">
                                Версия {selectedVersion?.status || "unknown"} только для чтения. Создание предмета доступно только в draft.
                            </div>
                        )}

                        <Form layout='vertical' className="space-y-4" onSubmit={onSubmit}>
                            <Form.Item label="systemName">
                                <Input
                                    className="input w-full"
                                    value={systemName}
                                    onChange={(e) => setSystemName(e.target.value)}
                                    placeholder="iron_ingot"
                                    required
                                />
                            </Form.Item>

                            <Form.Item label="Название">
                                <Input
                                    className="input w-full"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder="Iron Ingot"
                                    required
                                />
                            </Form.Item>

                            <Form.Item label="Описание">
                                <Input.TextArea
                                    className="input"
                                    rows={3}
                                    value={description}
                                    onChange={(e) => setDescription(e.target.value)}
                                    placeholder="Описание для UI"
                                />
                            </Form.Item>
                            <Flex vertical label="Группы параметров">
                                <h2 className="text-sm font-medium">Группа параметров</h2>
                                <div className="max-h-36 overflow-auto rounded-xl border border-border p-3">
                                    {loadingGroups ? (
                                        <div className="eyebrow">Загрузка...</div>
                                    ) : groups.length > 0 ? (
                                        // Используем контролируемый CheckboxGroup.Root
                                        <Checkbox.Group
                                            title="Выберите группы параметров"
                                            label="Выберите группы параметров"
                                            value={selectedGroups.map(String)}
                                            onChange={(values) => {
                                                // values возвращает массив выбранных строк. 
                                                // Если id в вашей базе числовые, раскомментируйте .map(Number)
                                                // toggleGroup(values.map(Number)); 

                                                // Если вы хотите сохранить старую функцию toggleGroup для одного элемента,
                                                // определяем, какой элемент был добавлен или удален:
                                                const next = values.map(String);
                                                const current = selectedGroups.map(String);
                                                const changed = next.filter(x => !current.includes(x)).concat(current.filter(x => !next.includes(x)))[0];
                                                if (changed) toggleGroup(changed); // или Number(changed)
                                            }}
                                            className="space-y-2 flex flex-col"
                                        >
                                            {groups.map((group) => {
                                                const groupId = String(group.id);

                                                return (
                                                    <Checkbox
                                                        key={groupId}
                                                        value={groupId}
                                                        className="flex items-start gap-2 "
                                                    >
                                                        <div className="text-sm">
                                                            <div>{group.name || group.systemName}</div>
                                                            <div className="card-subtle text-xs text-gray-500">{group.systemName}</div>
                                                        </div>
                                                    </Checkbox>
                                                );
                                            })}
                                        </Checkbox.Group>
                                    ) : (
                                        <div className="eyebrow">Нет групп параметров</div>
                                    )}
                                </div>
                            </Flex>

                            <Flex vertical label="Параметры">
                                <Card title="Задать параметр" className="">
                                    {parameterRows.map((row, index) => {
                                        const parameter = parametersById.get(row.parameterId);
                                        return (
                                            <Flex vertical flex align="flex-start" key={`${index}-${row.parameterId || "new"}`} className="mb-4 rounded-xl border border-border p-3 space-y-3">
                                                <div className="md:col-span-5">
                                                    <label className="eyebrow mb-1 block">Параметр</label>
                                                    <Select
                                                        className="select w-full"
                                                        value={row.parameterId}
                                                        onChange={(value) => updateParameterRow(index, { parameterId: value, value: "" })}
                                                    >
                                                        <Select.Option value="">Выберите параметр</Select.Option>
                                                        {parameters.map((item) => {
                                                            const itemId = item.id || item.parameterId;
                                                            return (
                                                                <Select.Option key={itemId} value={itemId}>
                                                                    {item.name || item.systemName} ({item.valueTypeCode || item.valueType})
                                                                </Select.Option>
                                                            );
                                                        })}
                                                    </Select>
                                                </div>
                                                <Flex align="end" gap="small" className="w-full">
                                                    <div className="md:col-span-6 flex-1">
                                                        <label className="eyebrow mb-1 block">Значение</label>
                                                        <ParamValueField
                                                            projectId={projectId}
                                                            versionId={versionId}
                                                            parameter={parameter}
                                                            value={row.value}
                                                            onChange={(value) => updateParameterRow(index, { value })}
                                                        />
                                                    </div>
                                                    <Button type="default" onClick={() => removeParameterRow(index)}>
                                                        Удалить
                                                    </Button>
                                                </Flex>
                                            </Flex>
                                        );
                                    })}

                                    <button type="button" className="btn btn-outline" onClick={addParameterRow}>
                                        Добавить параметр
                                    </button>
                                </Card>
                            </Flex>
                        </Form>
                    </UiDialogBody>

                    <UiDialogFooter>
                        <button
                            type="button"
                            className="btn btn-soft-accent"
                            onClick={onSubmit}
                            disabled={!canSubmit}
                        >
                            {submitting ? "Создаем..." : canMutate ? "Создать" : "Только draft"}
                        </button>
                        <button type="button" className="btn btn-outline" onClick={onClose}>
                            Отмена
                        </button>
                    </UiDialogFooter>
                </UiDialogContent>
            </UiDialogRoot>
        </App>
    );
}

NewItemDialog.propTypes = {
    open: PropTypes.bool,
    onClose: PropTypes.func,
    onCreated: PropTypes.func,
};
